import React, { useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import { Noticeable } from '@honeybadger-io/core/build/src/types';
import { Close } from '@mui/icons-material';
import {
  Alert,
  Grid,
  Link,
  Modal,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import logger from 'logger';
import Markdown from 'marked-react';
import { CheckoutStartedEventProps } from 'src/analytics/interfaces';
import useAnalytics from 'src/analytics/useAnalytics';
import UserContext from 'src/context/user';
import {
  GET_PROGRAMS,
  VALIDATE_ADDRESS_FOR_PROGRAM,
} from 'src/graphql-operations/catalog-service/programs.query';
import useNonNullContext from 'src/hooks/useNonNullContext';
import Custom404 from 'src/pages/404';
import {
  ProgramsQuery,
  ProgramsQueryVariables,
  ValidateAddressForProgramQuery,
  ValidateAddressForProgramQueryVariables,
} from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import {
  ProgramAddress as Address,
  StripeCheckoutSessionPayload,
} from 'src/ts/runtypes';
import { ClientNames } from 'src/utils/apolloClient';
import {
  GENERIC_ERROR_MESSAGE,
  PROGRAM_CONFIRMATION_KEYS,
} from 'src/utils/constants';
import { centsToDollars } from 'src/utils/currency';

import FunnelFooter from '../Footer/FunnelFooter';
import ImageDynamicSrc from '../ImageDynamicSrc';
import Layout from '../Layout';
import Loading from '../Loading';
import FunnelNavBarHeader from '../NavBarHeader/FunnelNavBarHeader';
import CatalogPreview from './CatalogPreview';
import ProgramCheckoutForm from './ProgramCheckoutForm';
import WhatToExpect from './WhatToExpect';
import { axiosClient } from 'src/utils/axiosClient';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { loadStripePromise } from '../../services/stripe';
import { getMetroForZip } from '../../services/rails-service';
import { omit } from 'lodash';

interface Props {
  programId: number;
}

type ExtraConfig = {
  heroImg: {
    sm: string;
    md: string;
    lg: string;
  };
} | null;

// Per Stripe, this must done outside of the component to avoid loading another promise on each render
const stripePromise = loadStripePromise();

const ProgramShop: React.FC<Props> = ({ programId }) => {
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );
  const { track } = useAnalytics();
  const theme = useTheme();

  const { tenantId } = useNonNullContext(UserContext);

  const [addressValidationError, setAddressValidationError] = React.useState<
    string | undefined
  >();
  const [isPurchaseWindowClosed, setIsPurchaseWindowClosed] =
    React.useState(false);
  const [metroDetails, setMetroDetails] = React.useState<
    MetroDetails | undefined
  >();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const [stripeClientSecret, setStripeClientSecret] = React.useState<
    string | undefined
  >();

  const {
    data: programsRes,
    error: getProgramError,
    loading: getProgramLoading,
  } = useQuery<ProgramsQuery, ProgramsQueryVariables>(GET_PROGRAMS, {
    variables: {
      input: {
        filter: {
          ids: [programId],
          tenantIds: [tenantId],
          sellable: true,
        },
        limit: 1,
      },
    },
    context: {
      clientName: ClientNames.CATALOG_SERVICE_GRAPH,
    },
    fetchPolicy: 'no-cache',
  });

  const program = programsRes ? programsRes.programs[0] : undefined;

  const [discountedGrandTotalInCents, setDiscountedGrandTotalInCents] =
    useState(program?.totalPriceInCents);

  /**
   * Maps server error messages on validation to copy for designs on client.
   */
  const ADDRESS_VALIDATION_ERROR_MESSAGES: Record<string, string> = {
    'The provided address is not within the service area of this program.':
      'We’re sorry, delivery isn’t available to this area yet.',
    'The provided address is not recognized. Please check your address and try again.':
      'This address looks like it might not be entered correctly. Please double check and try again.',
  };

  const [validateAddressForProgram] = useLazyQuery<
    ValidateAddressForProgramQuery,
    ValidateAddressForProgramQueryVariables
  >(VALIDATE_ADDRESS_FOR_PROGRAM, {
    fetchPolicy: 'no-cache',
  });

  const handleCheckout = async (address: Address) => {
    try {
      const payload: StripeCheckoutSessionPayload = {
        programId,
        email: address.email,
        address: omit(address, 'giftNotes', 'email', 'discountCode'),
        giftNotes: address.giftNotes,
        discountCode: address.discountCode,
      };

      const checkoutSession = await axiosClient.post<{ clientSecret: string }>(
        '/api/stripe-checkout-session',
        payload,
      );
      const { clientSecret } = checkoutSession.data;
      // @TODO: Handle error state
      setStripeClientSecret(clientSecret);

      // Event tracking should never hinder checkout, so catch all errors that could be thrown from it.
      try {
        if (program) {
          const deliveryTotal =
            program.deliveryFeePerWeekInCents * program.durationInWeeks;
          await track<CheckoutStartedEventProps>('Checkout Started', {
            value: centsToDollars(program.totalPriceInCents),
            revenue: centsToDollars(program.totalPriceInCents - deliveryTotal),
            shipping: centsToDollars(deliveryTotal),
            tax: 0,
            discount: 0,
            currency: 'USD',
            is_program: true,
            products: [
              {
                product_id: program.id.toString(),
                sku: program.id.toString(),
                name: program.title,
                price: centsToDollars(program.totalPriceInCents),
                quantity: 1,
              },
            ],
          });
          localStorage.setItem(
            PROGRAM_CONFIRMATION_KEYS.programId,
            program.id.toString(),
          );
          localStorage.setItem(
            PROGRAM_CONFIRMATION_KEYS.tenantId,
            program.tenantId.toString(),
          );
        }
      } catch (e: unknown) {
        logger.error({
          msg: 'Failed to track Checkout Started event',
          noticable: e as Noticeable,
          extra: {
            context: {
              programId: program?.id,
            },
          },
        });
      }
      // redirect to stripe checkout page
      // window.location.href = url;
    } catch (error: any) {
      const errorText = await error.response.text();
      if (errorText === 'Program purchase window is closed.') {
        setIsPurchaseWindowClosed(true);
      } else {
        logger.error({
          msg: 'Stripe checkout session creation failed unexpectedly',
          noticable: error,
        });
      }
    }
  };

  const handleSubmitAddress = async (newAddress: Address) => {
    setIsLoading(true);
    const { addressLine1, addressLine2, zipCode, city, state } = newAddress;
    const validationResponse = await validateAddressForProgram({
      context: {
        clientName: ClientNames.CATALOG_SERVICE_GRAPH,
      },
      variables: {
        programId,
        address: {
          addressLine1,
          addressLine2,
          city,
          zipCode,
          state,
        },
      },
    });
    if (validationResponse.data) {
      // clear out error message if exists for if / when user hits back button
      setAddressValidationError(undefined);
      await handleCheckout(newAddress);
    } else if (validationResponse.error) {
      const graphQLErrors = (
        validationResponse.error as {
          graphQLErrors?: { message?: string }[];
        }
      )?.graphQLErrors;
      const errorMsg = graphQLErrors?.[0]?.message;
      // map the server error message over to copy for client error
      setAddressValidationError(
        (errorMsg && ADDRESS_VALIDATION_ERROR_MESSAGES[errorMsg]) ??
          GENERIC_ERROR_MESSAGE,
      );
    }

    setIsLoading(false);
  };

  const [isPostalCodeSupported, setIsPostalCodeSupported] =
    React.useState(true);

  const [isLoading, setIsLoading] = React.useState(false);

  const verifyPostalCodeSupport = async (
    postalCode: string,
  ): Promise<boolean> => {
    setIsLoading(true);

    const metro = await getMetroForZip({
      zipcode: postalCode,
      tenantId,
    });

    const isSupported = !!program?.programMetros.find(
      (programMetro) => programMetro.metroId === metro?.id,
    );
    if (isSupported) {
      setMetroDetails({
        id: metro.id,
        type: metro.regionalShipping ? 'rse' : 'local',
        deliveryWeek: metro.deliveryWeek,
        postalCode,
        upcomingDeliveryDates: metro.deliveryDates,
        estimatedTransitTime: metro.estimatedTransitTime,
      });
    }

    setIsPostalCodeSupported(isSupported);
    setIsLoading(false);

    return isSupported;
  };

  const heroImgUrlSet = (program?.extraConfig as ExtraConfig)?.heroImg;

  if (getProgramLoading) {
    return (
      <Layout hideHeader isFunnelPage>
        <FunnelNavBarHeader />
        <Box
          sx={{
            paddingTop: '5rem',
          }}
        >
          <Loading />
        </Box>
      </Layout>
    );
  }

  if (getProgramError || (!getProgramLoading && !program)) {
    return <Custom404 />;
  }

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    // not using Layout component due to requirement for full size background colors which we don't typically do.
    <>
      <FunnelNavBarHeader />
      <Box
        sx={{
          paddingTop: isSmallScreen ? '3rem' : '4rem',
        }}
      >
        {program && (
          <>
            <Modal
              open={isModalOpen}
              onClose={closeModal}
              sx={{
                ['.MuiBackdrop-root']: {
                  backdropFilter: 'blur(4px)',
                },
                boxShadow: 24,
                textAlign: 'center',
                overflowY: 'auto',
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '3rem',
                alignItems: 'flex-start',
              }}
            >
              <Paper
                elevation={24}
                sx={{ width: isSmallScreen ? '100%' : '32rem' }}
              >
                <Box
                  sx={{
                    padding: '20px',
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '2px',
                    '&:focus-visible': {
                      outline: 'none',
                    },
                    position: 'relative',
                  }}
                >
                  <Box
                    onClick={closeModal}
                    sx={{
                      position: 'absolute',
                      cursor: 'pointer',
                      right: 5,
                      top: 5,
                    }}
                  >
                    <Close />
                  </Box>
                  {metroDetails ? (
                    <CatalogPreview
                      programId={programId}
                      metroDetails={metroDetails}
                      tenantId={tenantId}
                      closeModal={closeModal}
                    />
                  ) : (
                    <>Loading</>
                  )}
                </Box>
              </Paper>
            </Modal>
            <Grid container>
              <Grid
                sx={{
                  position: 'relative',
                  height: isSmallScreen ? '15rem' : 'auto',
                }}
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                }}
              >
                {heroImgUrlSet ? (
                  <ImageDynamicSrc
                    src={heroImgUrlSet}
                    priority
                    quality={100}
                    layout="fill"
                    objectFit="cover"
                    alt="Photo of the type of food you will recieve as a customer of this program"
                  />
                ) : undefined}
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                }}
              >
                <Grid
                  container
                  spacing={0}
                  sx={{
                    flexDirection: 'column',
                    alignContent: 'center',
                    textAlign: 'center',
                    maxWidth: '35rem',
                    margin: 'auto',
                    paddingTop: '4rem',
                  }}
                >
                  <Grid>
                    <Typography variant="h4">{program.title}</Typography>
                  </Grid>
                  <Grid
                    sx={{
                      padding: '1rem',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      <Markdown gfm breaks>
                        {program.description}
                      </Markdown>
                    </Typography>
                  </Grid>
                  <Grid>
                    {program?.crossedOutPriceInCents && (
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: theme.palette.grey[600],
                          textDecoration: 'line-through',
                        }}
                      >
                        ${(program.crossedOutPriceInCents / 100).toFixed(2)}
                      </Typography>
                    )}
                    <Typography variant="h5">
                      $
                      {(
                        (discountedGrandTotalInCents ||
                          program.totalPriceInCents) / 100
                      ).toFixed(2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                    >
                      delivery fee included
                    </Typography>
                  </Grid>
                  <Grid
                    sx={{
                      paddingTop: '2rem',
                    }}
                  >
                    {!!stripeClientSecret && (
                      <Typography
                        sx={{ paddingBottom: '2rem' }}
                        variant={'subtitle2'}
                      >
                        <Link
                          sx={{
                            color: theme.palette.text.secondary,
                            cursor: 'pointer',
                          }}
                          onClick={() => setStripeClientSecret(undefined)}
                        >
                          Change Delivery Address
                        </Link>
                      </Typography>
                    )}
                  </Grid>
                  <Grid
                    sx={{
                      px: 1,
                    }}
                  >
                    {!stripeClientSecret && (
                      <>
                        <ProgramCheckoutForm
                          handleSubmitAddress={handleSubmitAddress}
                          addressValidationError={addressValidationError}
                          clearAddressValidationError={() => {
                            setAddressValidationError(undefined);
                          }}
                          openCatalogPreview={() => setIsModalOpen(true)}
                          isPostalCodeSupported={isPostalCodeSupported}
                          verifyPostalCodeSupport={verifyPostalCodeSupport}
                          isLoading={isLoading}
                          programId={programId}
                          setDiscountedGrandTotalInCents={
                            setDiscountedGrandTotalInCents
                          }
                        />
                        {isPurchaseWindowClosed && (
                          <Grid>
                            <Alert severity="error">
                              We’re sorry, today’s ordering window has closed.
                              Please try again after midnight when it reopens.
                            </Alert>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                  {!!stripeClientSecret && (
                    <Grid
                      sx={{
                        px: 1,
                      }}
                    >
                      <EmbeddedCheckoutProvider
                        stripe={stripePromise}
                        options={{ clientSecret: stripeClientSecret }}
                      >
                        <EmbeddedCheckout />
                      </EmbeddedCheckoutProvider>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Box
              sx={{
                paddingTop: '6rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                marginBottom: '3rem',
              }}
            >
              <WhatToExpect />
            </Box>
            <FunnelFooter isShowingCart={false} />
          </>
        )}
      </Box>
    </>
  );
};

export interface DeliveryDetails {
  type: 'local' | 'rse';
  upcomingDeliveries: {
    deliveryDate: string;
    estimatedDeliveryDate?: string;
  }[];
}

export interface MetroDetails {
  id: number;
  type: 'local' | 'rse';
  deliveryWeek: string;
  postalCode: string;
  upcomingDeliveryDates: string[];
  estimatedTransitTime: number;
}

export default ProgramShop;
